import { useState, useEffect } from 'react';
import type { IAsset } from '../providers';
import { BinIcon, EditIcon } from '../../../icons';
import { MultiSelectDropdown } from '../../UI';
import { useAssetActions } from '../context/AssetActionsContext';
import { ZprAccordion, ZprAccordionPanel, ZprLabel } from '@zpr-stencil';
import { useLabels } from '../hooks';

export interface LabelsAccordionProps {
  asset: IAsset;
  isEditableAsset?: boolean;
  canEditLabels?: boolean;
  canAssignLabels?: boolean;
}

export const LabelsAccordion = ({ asset, isEditableAsset = true, canEditLabels = true, canAssignLabels = true }: LabelsAccordionProps) => {
  const { zapworksProvider, workspaceId, onUpdateLabels, onCreateLabel, onEditLabel, onDeleteLabel } = useAssetActions();
  const { labels: availableLabels, getLabelNameByUid, getLabelObjectByUid, createLabel, updateLabel, deleteLabel } = useLabels(zapworksProvider, workspaceId);

  const metadata = asset.getMetadata();
  const initialUids = Array.isArray(metadata.labels) ? metadata.labels.map((l: any) => l.uid as string) : [];

  const [selectedUids, setSelectedUids] = useState<string[]>(initialUids);

  useEffect(() => {
    const meta = asset.getMetadata();
    const uids = Array.isArray(meta.labels) ? meta.labels.map((l: any) => l.uid as string) : [];
    setSelectedUids(uids);
  }, [asset]);

  const handleLabelsChange = (uids: string[]) => {
    setSelectedUids(uids);
    onUpdateLabels?.(asset, availableLabels.filter(l => uids.includes(l.uid)));
  };

  const handleRemoveLabel = (uid: string) => {
    const newUids = selectedUids.filter(id => id !== uid);
    setSelectedUids(newUids);
    onUpdateLabels?.(asset, availableLabels.filter(l => newUids.includes(l.uid)));
  };

  return (
    <ZprAccordion className="no-background">
      <ZprAccordionPanel isOpen={true}>
        <span slot="accordion-header">Labels</span>
        <div slot="accordion-body">
          {selectedUids.length > 0 &&
            <div className="asset-detail-panel__section">
              <div className="labels-container">
                  {selectedUids.map((uid) => (
                    <ZprLabel
                      key={uid}
                      text={getLabelNameByUid(uid)}
                      color={getLabelObjectByUid(uid)?.color}
                      size='small'
                      canRemove={canAssignLabels}
                      onLabelRemove={() => handleRemoveLabel(uid)}
                    />
                  ))}
              </div>
            </div>
          }
          {isEditableAsset && (
            <div className="asset-detail-panel__section">
              <MultiSelectDropdown
                placeholder="Manage labels"
                title="Labels"
                options={availableLabels.map(label => ({
                  value: label.uid,
                  label: getLabelNameByUid(label.uid),
                  selected: selectedUids.includes(label.uid),
                  color: label.color,
                }))}
                onChange={canAssignLabels ? handleLabelsChange : undefined}
                enableSearch
                showLabelOnly
                isLabelOptions
                direction='left'
                onCreate={canEditLabels ? (name) => createLabel({ name, color: '#EAEAEA' }).then(() => onCreateLabel?.(name)) : undefined}
                actions={canEditLabels ? [
                  {
                    icon: <EditIcon />,
                    label: 'Edit',
                    isEditLabel: true,
                    onClick: async (value: string, newLabel?: string, newColor?: string) => {
                      await updateLabel(value, { name: newLabel ?? '', color: newColor ?? '' });
                      onEditLabel?.(value, newLabel ?? '');
                    },
                  },
                  {
                    icon: <BinIcon width={16} height={16} className="hover-danger" />,
                    label: 'Delete',
                    isConfirmDelete: true,
                    onClick: async (value: string) => {
                      await deleteLabel(value);
                      onDeleteLabel?.(value);
                    },
                  },
                ] : undefined }
              />
            </div>
          )}
        </div>
      </ZprAccordionPanel>
    </ZprAccordion>
  );
};
